import { useState, useEffect } from 'react';
import { useOrg } from '../../contexts/OrgContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermission } from '../../hooks/usePermission';
import {
  getSchedules,
  createSchedule,
  updateSchedule,
  deleteSchedule,
  toggleScheduleActive,
  processSchedules,
  CreateScheduleData,
} from '../../services/scheduleService';
import { getAssets } from '../../services/assetService';
import { getSites } from '../../services/siteService';
import { getTemplates } from '../../services/templateService';
import { getOrganizationUsers } from '../../services/userService';
import {
  InspectionSchedule,
  Asset,
  Site,
  FormTemplate,
  RecurrenceFrequency,
  InspectionPriority,
  User,
} from '@easydone/shared';
import {
  PlusIcon,
  CalendarIcon,
  PlayIcon,
  PauseIcon,
  PencilIcon,
  TrashIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';

export function SchedulesPage() {
  const { currentOrg } = useOrg();
  const { currentUser } = useAuth();
  const canManageSchedules = usePermission('inspections.manage');

  const [schedules, setSchedules] = useState<InspectionSchedule[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<InspectionSchedule | null>(null);

  useEffect(() => {
    loadData();
  }, [currentOrg]);

  const loadData = async () => {
    if (!currentOrg) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [schedulesData, assetsData, sitesData, templatesData, usersData] = await Promise.all([
        getSchedules(currentOrg.id),
        getAssets(currentOrg.id),
        getSites(currentOrg.id),
        getTemplates(currentOrg.id),
        getOrganizationUsers(currentOrg.id),
      ]);
      setSchedules(schedulesData);
      setAssets(assetsData);
      setSites(sitesData);
      setTemplates(templatesData);
      setUsers(usersData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSchedule = async (data: CreateScheduleData) => {
    if (!currentOrg || !currentUser) return;

    try {
      await createSchedule(currentOrg.id, currentUser.uid, data);
      setShowCreateModal(false);
      loadData();
    } catch (error) {
      console.error('Failed to create schedule:', error);
      alert('Failed to create schedule');
    }
  };

  const handleUpdateSchedule = async (scheduleId: string, data: Partial<CreateScheduleData>) => {
    if (!currentOrg) return;

    try {
      await updateSchedule(currentOrg.id, scheduleId, data);
      setEditingSchedule(null);
      loadData();
    } catch (error) {
      console.error('Failed to update schedule:', error);
      alert('Failed to update schedule');
    }
  };

  const handleToggleActive = async (scheduleId: string, isActive: boolean) => {
    if (!currentOrg) return;

    try {
      await toggleScheduleActive(currentOrg.id, scheduleId, !isActive);
      loadData();
    } catch (error) {
      console.error('Failed to toggle schedule:', error);
      alert('Failed to toggle schedule');
    }
  };

  const handleDeleteSchedule = async (scheduleId: string) => {
    if (!currentOrg) return;
    if (!confirm('Are you sure you want to delete this schedule? This will not delete existing inspections.')) return;

    try {
      await deleteSchedule(currentOrg.id, scheduleId);
      loadData();
    } catch (error) {
      console.error('Failed to delete schedule:', error);
      alert('Failed to delete schedule');
    }
  };

  const handleGenerateInspections = async () => {
    if (!currentOrg || !currentUser) return;

    setProcessing(true);
    try {
      const result = await processSchedules(currentOrg.id, currentUser.uid, currentUser.displayName || currentUser.email || 'User');

      if (result.errors.length > 0) {
        alert(`Generated ${result.generated} inspection(s)\n\nErrors: ${result.errors.length}\n${result.errors.map(e => `- ${e.error}`).join('\n')}`);
      } else {
        alert(`Successfully generated ${result.generated} inspection(s) from active schedules!`);
      }

      loadData(); // Reload schedules to show updated next inspection dates
    } catch (error) {
      console.error('Failed to process schedules:', error);
      alert('Failed to generate inspections');
    } finally {
      setProcessing(false);
    }
  };

  const getAssetName = (assetId: string) => {
    return assets.find(a => a.id === assetId)?.name || 'Unknown Asset';
  };

  const getTemplateName = (templateId: string) => {
    return templates.find(t => t.id === templateId)?.name || 'Unknown Template';
  };

  const formatRecurrence = (schedule: InspectionSchedule) => {
    const { frequency, interval } = schedule.recurrence;
    const intervalText = interval > 1 ? `Every ${interval} ` : '';

    switch (frequency) {
      case 'daily':
        return `${intervalText}Day${interval > 1 ? 's' : ''}`;
      case 'weekly':
        return `${intervalText}Week${interval > 1 ? 's' : ''}`;
      case 'monthly':
        return `${intervalText}Month${interval > 1 ? 's' : ''}`;
      case 'yearly':
        return `${intervalText}Year${interval > 1 ? 's' : ''}`;
      default:
        return `Every ${interval} days`;
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Not set';
    const date = timestamp.toDate();
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading schedules...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inspection Schedules</h1>
          <p className="text-gray-600 mt-1">
            Manage recurring inspection schedules for your assets
          </p>
        </div>
        {canManageSchedules && (
          <div className="flex items-center space-x-3">
            <button
              onClick={handleGenerateInspections}
              disabled={processing || schedules.length === 0}
              className="btn btn-secondary flex items-center"
              title="Generate inspections from active schedules"
            >
              <BoltIcon className={`w-5 h-5 mr-2 ${processing ? 'animate-spin' : ''}`} />
              {processing ? 'Generating...' : 'Generate Now'}
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary flex items-center"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              New Schedule
            </button>
          </div>
        )}
      </div>

      {/* Schedules List */}
      {schedules.length === 0 ? (
        <div className="card text-center py-12">
          <CalendarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No schedules yet</h3>
          <p className="text-gray-600 mb-4">
            Create recurring inspection schedules to automate your workflow
          </p>
          {canManageSchedules && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary"
            >
              Create Schedule
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {schedules.map((schedule) => (
            <div
              key={schedule.id}
              className={`card hover:shadow-lg transition-shadow ${
                !schedule.isActive ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {schedule.name}
                    </h3>
                    {!schedule.isActive && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        Inactive
                      </span>
                    )}
                  </div>
                  {schedule.description && (
                    <p className="text-sm text-gray-600 mb-3">{schedule.description}</p>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Asset:</span>
                      <p className="font-medium text-gray-900">{getAssetName(schedule.assetId)}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Template:</span>
                      <p className="font-medium text-gray-900">{getTemplateName(schedule.templateId)}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Frequency:</span>
                      <p className="font-medium text-gray-900">{formatRecurrence(schedule)}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Next Inspection:</span>
                      <p className="font-medium text-gray-900">
                        {formatDate(schedule.nextInspectionDate)}
                      </p>
                    </div>
                  </div>
                </div>

                {canManageSchedules && (
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handleToggleActive(schedule.id, schedule.isActive)}
                      className="p-2 text-gray-400 hover:text-primary-600"
                      title={schedule.isActive ? 'Pause Schedule' : 'Activate Schedule'}
                    >
                      {schedule.isActive ? (
                        <PauseIcon className="w-5 h-5" />
                      ) : (
                        <PlayIcon className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      onClick={() => setEditingSchedule(schedule)}
                      className="p-2 text-gray-400 hover:text-primary-600"
                      title="Edit Schedule"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteSchedule(schedule.id)}
                      className="p-2 text-gray-400 hover:text-red-600"
                      title="Delete Schedule"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingSchedule) && (
        <ScheduleModal
          schedule={editingSchedule}
          assets={assets}
          sites={sites}
          templates={templates}
          users={users}
          onClose={() => {
            setShowCreateModal(false);
            setEditingSchedule(null);
          }}
          onSave={(data) => {
            if (editingSchedule) {
              handleUpdateSchedule(editingSchedule.id, data);
            } else {
              handleCreateSchedule(data);
            }
          }}
        />
      )}
    </div>
  );
}

// Schedule Modal Component continues in next part...
interface ScheduleModalProps {
  schedule: InspectionSchedule | null;
  assets: Asset[];
  sites: Site[];
  templates: FormTemplate[];
  users: User[];
  onClose: () => void;
  onSave: (data: CreateScheduleData) => void;
}

function ScheduleModal({ schedule, assets, sites, templates, users, onClose, onSave }: ScheduleModalProps) {
  const [formData, setFormData] = useState<CreateScheduleData>({
    name: schedule?.name || '',
    description: schedule?.description || '',
    assetId: schedule?.assetId || (assets[0]?.id || ''),
    siteId: schedule?.siteId || (sites[0]?.id || ''),
    templateId: schedule?.templateId || (templates[0]?.id || ''),
    startDate: schedule?.startDate
      ? (typeof schedule.startDate === 'number'
          ? new Date(schedule.startDate)
          : (schedule.startDate as any).toDate())
      : new Date(),
    recurrence: schedule?.recurrence || {
      frequency: 'monthly' as RecurrenceFrequency,
      interval: 1,
    },
    priority: schedule?.priority || ('routine' as InspectionPriority),
    assignedTo: schedule?.assignedTo,
    leadTime: schedule?.leadTime || 0,
    approvalRequired: schedule?.approvalRequired || false,
    approvalLevel: schedule?.approvalLevel,
    approvalUserId: schedule?.approvalUserId,
  });

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          {schedule ? 'Edit Schedule' : 'Create New Schedule'}
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Basic Info */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Schedule Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input w-full"
                placeholder="e.g., Monthly Pump Inspection"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input w-full"
                rows={2}
                placeholder="Optional description"
              />
            </div>

            {/* Asset Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asset *
                </label>
                <select
                  value={formData.assetId}
                  onChange={(e) => {
                    const asset = assets.find(a => a.id === e.target.value);
                    setFormData({
                      ...formData,
                      assetId: e.target.value,
                      siteId: asset?.siteId || formData.siteId,
                    });
                  }}
                  className="input w-full"
                  required
                >
                  {assets.map(asset => (
                    <option key={asset.id} value={asset.id}>
                      {asset.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template *
                </label>
                <select
                  value={formData.templateId}
                  onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
                  className="input w-full"
                  required
                >
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Recurrence Settings */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="font-medium text-gray-900 mb-3">Recurrence Settings</h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Frequency *
                  </label>
                  <select
                    value={formData.recurrence.frequency}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        recurrence: {
                          ...formData.recurrence,
                          frequency: e.target.value as RecurrenceFrequency,
                        },
                      })
                    }
                    className="input w-full"
                    required
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Every *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.recurrence.interval}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        recurrence: {
                          ...formData.recurrence,
                          interval: parseInt(e.target.value),
                        },
                      })
                    }
                    className="input w-full"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.startDate.toISOString().split('T')[0]}
                    onChange={(e) =>
                      setFormData({ ...formData, startDate: new Date(e.target.value) })
                    }
                    className="input w-full"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority *
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) =>
                      setFormData({ ...formData, priority: e.target.value as InspectionPriority })
                    }
                    className="input w-full"
                    required
                  >
                    <option value="routine">Routine</option>
                    <option value="urgent">Urgent</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assigned To
                  </label>
                  <select
                    value={formData.assignedTo || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, assignedTo: e.target.value || undefined })
                    }
                    className="input w-full"
                  >
                    <option value="">Unassigned</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.displayName || user.email}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    User responsible for completing inspections
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lead Time (days)
                  </label>
                <input
                  type="number"
                  min="0"
                  value={formData.leadTime}
                  onChange={(e) =>
                    setFormData({ ...formData, leadTime: parseInt(e.target.value) })
                  }
                  className="input w-full"
                  placeholder="Days before due date to create inspection"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Inspections will be created this many days before the scheduled date
                </p>
              </div>
            </div>
            </div>

            {/* Approval Settings */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="font-medium text-gray-900 mb-3">Approval Settings</h3>

              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="approvalRequired"
                    checked={formData.approvalRequired}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        approvalRequired: e.target.checked,
                        ...((!e.target.checked) && { approvalLevel: undefined, approvalUserId: undefined }),
                      })
                    }
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="approvalRequired" className="ml-2 block text-sm text-gray-900">
                    Require approval after inspection completion
                  </label>
                </div>

                {formData.approvalRequired && (
                  <div className="ml-6 space-y-3 pl-4 border-l-2 border-gray-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Specific Approver
                      </label>
                      <select
                        value={formData.approvalUserId || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            approvalUserId: e.target.value || undefined,
                            ...(e.target.value && { approvalLevel: undefined }),
                          })
                        }
                        className="input w-full"
                      >
                        <option value="">Any user with minimum role level (below)</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.displayName || user.email}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Leave blank to allow any user with the minimum role level
                      </p>
                    </div>

                    {!formData.approvalUserId && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Minimum Role Level
                        </label>
                        <select
                          value={formData.approvalLevel || ''}
                          onChange={(e) =>
                            setFormData({ ...formData, approvalLevel: e.target.value as any })
                          }
                          className="input w-full"
                        >
                          <option value="">Select role level</option>
                          <option value="team_leader">Team Leader or higher</option>
                          <option value="local_engineer">Local Engineer or higher</option>
                          <option value="central_engineer">Central Engineer or higher</option>
                          <option value="org_admin">Organization Admin only</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Users with this role or higher can approve inspections
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : schedule ? 'Update Schedule' : 'Create Schedule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
